import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import './VesselDetailView.css';

function VesselDetailView() {
  const { vesselSku } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [monthlyUsage, setMonthlyUsage] = useState([]);
  const [projections, setProjections] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadVesselData();
  }, [vesselSku]);

  const loadVesselData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [monthlyRes, projectionsRes, analysisRes] = await Promise.all([
        axios.get(`/api/material-planning/vessel/${vesselSku}/monthly?monthsBack=12`),
        axios.get(`/api/material-planning/vessel/${vesselSku}/projections`),
        axios.get(`/api/material-planning/vessel/${vesselSku}/analysis`)
      ]);

      setMonthlyUsage(monthlyRes.data || []);
      setProjections(projectionsRes.data || []);
      setAnalysis(analysisRes.data || null);
    } catch (err) {
      console.error('Error loading vessel data:', err);
      setError('Failed to load vessel data: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateProjections = async () => {
    try {
      setGenerating(true);
      await axios.post(`/api/material-planning/vessel/${vesselSku}/generate-projections`, {
        monthsAhead: 6
      });
      await loadVesselData();
      alert('Projections generated successfully!');
    } catch (err) {
      console.error('Error generating projections:', err);
      alert('Failed to generate projections: ' + (err.response?.data?.error || err.message));
    } finally {
      setGenerating(false);
    }
  };

  const getMonthName = (month) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[month - 1] || '';
  };

  const getTrendIcon = (direction) => {
    if (direction === 'increasing') return '↗';
    if (direction === 'decreasing') return '↘';
    return '→';
  };

  const getTrendClass = (direction) => {
    if (direction === 'increasing') return 'trend-up';
    if (direction === 'decreasing') return 'trend-down';
    return 'trend-stable';
  };

  // Calculate max value for chart scaling
  const allValues = [
    ...monthlyUsage.map(m => m.total_quantity),
    ...projections.map(p => p.projected_quantity)
  ];
  const maxValue = Math.max(...allValues, 100);

  if (loading) {
    return <div className="vessel-detail-loading">Loading vessel details...</div>;
  }

  if (error) {
    return (
      <div className="vessel-detail-error">
        <p>{error}</p>
        <button onClick={() => navigate('/material-planning')}>Back to Vessel List</button>
      </div>
    );
  }

  return (
    <div className="vessel-detail-view">
      <div className="header">
        <button onClick={() => navigate('/material-planning')} className="back-button">
          ← Back to Vessels
        </button>
        <div className="title-section">
          <h1>{vesselSku}</h1>
          {analysis && (
            <p className="vessel-name">{analysis.vessel_sku}</p>
          )}
        </div>
        <button
          onClick={handleGenerateProjections}
          disabled={generating}
          className="btn-generate"
        >
          {generating ? 'Generating...' : 'Generate Projections'}
        </button>
      </div>

      {analysis && (
        <div className="summary-cards">
          <div className="summary-card">
            <label>Data Points</label>
            <value>{analysis.data_points}</value>
            <small>months of history</small>
          </div>
          <div className="summary-card">
            <label>Total Usage</label>
            <value>{(analysis.statistics.total || 0).toLocaleString()}</value>
            <small>units</small>
          </div>
          <div className="summary-card">
            <label>Average Monthly</label>
            <value>{Math.round(analysis.statistics.mean || 0).toLocaleString()}</value>
            <small>units/month</small>
          </div>
          <div className="summary-card">
            <label>Peak Usage</label>
            <value>{(analysis.statistics.max || 0).toLocaleString()}</value>
            <small>units in one month</small>
          </div>
          <div className={`summary-card ${getTrendClass(analysis.trend?.direction)}`}>
            <label>Trend</label>
            <value>{getTrendIcon(analysis.trend?.direction)} {analysis.trend?.direction || 'Unknown'}</value>
            <small>slope: {(analysis.trend?.slope || 0).toFixed(2)}</small>
          </div>
          {analysis.seasonality && (
            <div className="summary-card">
              <label>Seasonality</label>
              <value>{analysis.seasonality.hasSeasonality ? 'Yes' : 'No'}</value>
              <small>{analysis.seasonality.hasSeasonality ? 'pattern detected' : 'no pattern'}</small>
            </div>
          )}
        </div>
      )}

      <div className="chart-section">
        <h2>Usage History & Projections</h2>
        <div className="chart-container">
          <div className="chart">
            {monthlyUsage.map((month, idx) => {
              const height = (month.total_quantity / maxValue) * 100;
              return (
                <div key={idx} className="bar-container">
                  <div
                    className="bar historical"
                    style={{ height: `${height}%` }}
                    title={`${getMonthName(month.month)} ${month.year}: ${month.total_quantity} units`}
                  >
                    <span className="bar-value">{month.total_quantity}</span>
                  </div>
                  <div className="bar-label">
                    {getMonthName(month.month)}<br/>{month.year}
                  </div>
                </div>
              );
            })}
            {projections.map((proj, idx) => {
              const height = (proj.projected_quantity / maxValue) * 100;
              const confidence = parseFloat(proj.confidence_score || 0);
              return (
                <div key={`proj-${idx}`} className="bar-container">
                  <div
                    className="bar projection"
                    style={{
                      height: `${height}%`,
                      opacity: 0.5 + (confidence * 0.5)
                    }}
                    title={`${getMonthName(proj.projection_month)} ${proj.projection_year}: ${proj.projected_quantity} units (${(confidence * 100).toFixed(0)}% confidence)`}
                  >
                    <span className="bar-value projected">{proj.projected_quantity}</span>
                  </div>
                  <div className="bar-label">
                    {getMonthName(proj.projection_month)}<br/>{proj.projection_year}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="chart-legend">
            <div className="legend-item">
              <span className="legend-color historical"></span>
              <span>Historical Usage</span>
            </div>
            <div className="legend-item">
              <span className="legend-color projection"></span>
              <span>Projected Usage</span>
            </div>
          </div>
        </div>
      </div>

      {projections.length > 0 && (
        <div className="projections-table">
          <h2>Detailed Projections</h2>
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th>Projected Quantity</th>
                <th>Confidence</th>
                <th>Method</th>
                <th>Baseline</th>
                <th>Seasonality Factor</th>
                <th>Trend Adj.</th>
              </tr>
            </thead>
            <tbody>
              {projections.map((proj, idx) => (
                <tr key={idx}>
                  <td>{getMonthName(proj.projection_month)} {proj.projection_year}</td>
                  <td className="quantity">{proj.projected_quantity.toLocaleString()}</td>
                  <td className="confidence">
                    <div className="confidence-bar">
                      <div
                        className="confidence-fill"
                        style={{ width: `${parseFloat(proj.confidence_score || 0) * 100}%` }}
                      ></div>
                      <span>{(parseFloat(proj.confidence_score || 0) * 100).toFixed(0)}%</span>
                    </div>
                  </td>
                  <td className="method">{proj.projection_method}</td>
                  <td>{proj.baseline_usage || 'N/A'}</td>
                  <td>{proj.seasonality_factor ? parseFloat(proj.seasonality_factor).toFixed(2) : 'N/A'}</td>
                  <td className={parseFloat(proj.trend_adjustment || 0) > 0 ? 'positive' : 'negative'}>
                    {proj.trend_adjustment || '0'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {analysis && analysis.anomalies && analysis.anomalies.length > 0 && (
        <div className="anomalies-section">
          <h2>Usage Anomalies</h2>
          <p className="section-description">
            Months with unusual usage patterns (>2 standard deviations from mean)
          </p>
          <div className="anomalies-list">
            {analysis.anomalies.map((anomaly, idx) => (
              <div key={idx} className={`anomaly-card ${anomaly.type}`}>
                <div className="anomaly-header">
                  <span className="anomaly-date">
                    {getMonthName(anomaly.month)} {anomaly.year}
                  </span>
                  <span className={`anomaly-type ${anomaly.type}`}>
                    {anomaly.type === 'spike' ? '↑ Spike' : '↓ Dip'}
                  </span>
                </div>
                <div className="anomaly-value">
                  {anomaly.quantity.toLocaleString()} units
                </div>
                <div className="anomaly-deviation">
                  Deviation: {anomaly.deviation}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="monthly-details">
        <h2>Monthly Usage Details</h2>
        <table>
          <thead>
            <tr>
              <th>Month</th>
              <th>Total Quantity</th>
              <th>Work Orders</th>
              <th>Customers</th>
              <th>Avg per Order</th>
            </tr>
          </thead>
          <tbody>
            {monthlyUsage.map((month, idx) => (
              <tr key={idx}>
                <td>{getMonthName(month.month)} {month.year}</td>
                <td className="quantity">{month.total_quantity.toLocaleString()}</td>
                <td>{month.unique_work_orders || 0}</td>
                <td>{month.unique_customers || 0}</td>
                <td>{month.avg_quantity_per_order || '0'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="export-section">
        <h3>Export Data for AI Analysis</h3>
        <p>Download vessel usage data in CSV format for AI agent analysis</p>
        <button
          onClick={() => {
            window.location.href = `/api/material-planning/export?vesselSku=${vesselSku}&format=csv`;
          }}
          className="btn-export"
        >
          Export CSV
        </button>
      </div>
    </div>
  );
}

export default VesselDetailView;
